/**
 * RitualRoutineFlow - Main component for morning/evening ritual flow
 * Guides users through their custom routine with education and tracking
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { 
  Sunrise, Moon, Play, Pause, CheckCircle2, 
  ChevronRight, ChevronLeft, Sparkles, Clock,
  Trophy, ArrowRight
} from 'lucide-react';
import { RitualActivityCard } from './RitualActivityCard';
import { TomorrowActionsCard } from './TomorrowActionsCard';
import { 
  RitualActivity, 
  RoutineType, 
  DEFAULT_MORNING_ACTIVITIES, 
  DEFAULT_EVENING_ACTIVITIES 
} from './types';
import { cn } from '@/lib/utils';

interface RitualRoutineFlowProps {
  routineType: RoutineType;
  onComplete: (data: {
    items_completed: string[];
    total_duration_minutes: number;
    mood_before: number;
    mood_after: number;
    tomorrow_actions?: any;
  }) => void;
  onClose?: () => void;
  futureSelfIdentity?: string;
}

export const RitualRoutineFlow: React.FC<RitualRoutineFlowProps> = ({
  routineType,
  onComplete,
  onClose,
  futureSelfIdentity
}) => {
  const [currentStep, setCurrentStep] = useState<'intro' | 'mood-before' | 'activities' | 'tomorrow' | 'mood-after' | 'complete'>('intro');
  const [activities, setActivities] = useState<RitualActivity[]>([]);
  const [completedActivities, setCompletedActivities] = useState<Set<string>>(new Set());
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [moodBefore, setMoodBefore] = useState(5);
  const [moodAfter, setMoodAfter] = useState(5);
  const [tomorrowActions, setTomorrowActions] = useState<any>(null);
  const [startTime] = useState(new Date());

  useEffect(() => {
    // Load activities based on routine type
    const defaultActivities = routineType === 'morning' 
      ? DEFAULT_MORNING_ACTIVITIES 
      : DEFAULT_EVENING_ACTIVITIES;
    setActivities(defaultActivities);
  }, [routineType]);

  const toggleActivityComplete = (activityId: string) => {
    setCompletedActivities(prev => {
      const next = new Set(prev);
      if (next.has(activityId)) {
        next.delete(activityId);
      } else {
        next.add(activityId);
      }
      return next;
    });
  };

  const handleComplete = () => {
    const duration = Math.round((new Date().getTime() - startTime.getTime()) / 60000);
    onComplete({
      items_completed: Array.from(completedActivities),
      total_duration_minutes: duration,
      mood_before: moodBefore,
      mood_after: moodAfter,
      tomorrow_actions: routineType === 'evening' ? tomorrowActions : undefined
    });
  };

  const progress = currentStep === 'activities' 
    ? (completedActivities.size / activities.length) * 100
    : currentStep === 'complete' ? 100 : 0;

  const isMorning = routineType === 'morning';
  const Icon = isMorning ? Sunrise : Moon;
  const gradientClass = isMorning 
    ? "from-secondary/20 via-background to-primary/10"
    : "from-primary/20 via-background to-accent/10";

  return (
    <div className={cn("min-h-screen bg-gradient-to-br p-4 sm:p-6", gradientClass)}>
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-10 h-10 rounded-xl flex items-center justify-center",
              isMorning ? "bg-secondary/20" : "bg-primary/20"
            )}>
              <Icon className={cn("w-5 h-5", isMorning ? "text-secondary" : "text-primary")} />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-foreground">
                {isMorning ? "Morning Habit" : "Evening Habit"}
              </h1>
              <p className="text-xs text-muted-foreground">
                {activities.length} activities • ~{activities.reduce((acc, a) => acc + a.duration_minutes, 0)} min
              </p>
            </div>
          </div>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
          )}
        </div>

        {/* Progress */}
        {currentStep !== 'intro' && currentStep !== 'complete' && (
          <div className="mb-6">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground mt-1 text-center">
              {completedActivities.size} of {activities.length} activities complete
            </p>
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Intro Step */}
          {currentStep === 'intro' && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center py-12"
            >
              <div className={cn(
                "w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center",
                isMorning ? "bg-secondary/20" : "bg-primary/20"
              )}>
                <Icon className={cn("w-10 h-10", isMorning ? "text-secondary" : "text-primary")} />
              </div>
              
              <h2 className="text-2xl font-light text-foreground mb-3">
                {isMorning ? "Good Morning" : "Time to Wind Down"}
              </h2>
              <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
                {isMorning 
                  ? "Let's set yourself up for a powerful day ahead. These practices are scientifically proven to enhance your wellbeing."
                  : "Prepare your mind and body for restful sleep. Release the day and set intentions for tomorrow."
                }
              </p>

              <Button 
                size="lg" 
                onClick={() => setCurrentStep('mood-before')}
                className="gap-2"
              >
                <Play className="w-4 h-4" />
                Begin
              </Button>
            </motion.div>
          )}

          {/* Mood Before */}
          {currentStep === 'mood-before' && (
            <motion.div
              key="mood-before"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="border-0 shadow-lg">
                <CardHeader className="text-center">
                  <CardTitle className="text-lg font-medium">How are you feeling right now?</CardTitle>
                  <p className="text-sm text-muted-foreground">This helps track how habits affect your mood</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <p className="text-2xl font-light mt-2">{moodBefore}/10</p>
                  </div>
                  
                  <Slider
                    value={[moodBefore]}
                    onValueChange={([v]) => setMoodBefore(v)}
                    min={1}
                    max={10}
                    step={1}
                    className="py-4"
                  />

                  <Button 
                    onClick={() => setCurrentStep('activities')} 
                    className="w-full gap-2"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Activities */}
          {currentStep === 'activities' && (
            <motion.div
              key="activities"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-4"
            >
              {activities.map((activity, index) => (
                <RitualActivityCard
                  key={activity.id}
                  activity={activity}
                  isCompleted={completedActivities.has(activity.id)}
                  onToggleComplete={toggleActivityComplete}
                  showEducation={true}
                />
              ))}

              <Button 
                onClick={() => setCurrentStep(routineType === 'evening' ? 'tomorrow' : 'mood-after')} 
                className="w-full gap-2 mt-6"
                disabled={completedActivities.size === 0}
              >
                {completedActivities.size === activities.length ? (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    All Complete - Continue
                  </>
                ) : (
                  <>
                    Continue with {completedActivities.size} activities
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </Button>
            </motion.div>
          )}

          {/* Tomorrow Actions (Evening Only) */}
          {currentStep === 'tomorrow' && routineType === 'evening' && (
            <motion.div
              key="tomorrow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <TomorrowActionsCard
                mode="set"
                onSave={(data) => {
                  setTomorrowActions(data);
                  setCurrentStep('mood-after');
                }}
                futureSelfIdentity={futureSelfIdentity}
              />
            </motion.div>
          )}

          {/* Mood After */}
          {currentStep === 'mood-after' && (
            <motion.div
              key="mood-after"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="border-0 shadow-lg">
                <CardHeader className="text-center">
                  <CardTitle className="text-lg font-medium">How do you feel now?</CardTitle>
                  <p className="text-sm text-muted-foreground">After completing your routine</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center">
                    <p className="text-2xl font-light mt-2">{moodAfter}/10</p>
                  </div>
                  
                  <Slider
                    value={[moodAfter]}
                    onValueChange={([v]) => setMoodAfter(v)}
                    min={1}
                    max={10}
                    step={1}
                    className="py-4"
                  />

                  {moodAfter > moodBefore && (
                    <div className="p-3 rounded-lg bg-primary/10 text-center">
                      <Sparkles className="w-5 h-5 text-primary mx-auto mb-1" />
                      <p className="text-sm text-primary font-medium">
                        +{moodAfter - moodBefore} mood boost!
                      </p>
                    </div>
                  )}

                  <Button 
                    onClick={() => {
                      setCurrentStep('complete');
                      handleComplete();
                    }} 
                    className="w-full gap-2"
                  >
                    Complete
                    <CheckCircle2 className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Complete */}
          {currentStep === 'complete' && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-12"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2 }}
                className="w-20 h-20 rounded-full bg-primary/20 mx-auto mb-6 flex items-center justify-center"
              >
                <Trophy className="w-10 h-10 text-primary" />
              </motion.div>

              <h2 className="text-2xl font-light text-foreground mb-3">
                {isMorning ? "You're Ready!" : "Sweet Dreams"}
              </h2>
              <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                {isMorning 
                  ? `You completed ${completedActivities.size} activities. Your day is set for success.`
                  : `Beautiful work. You've set ${tomorrowActions ? '3 intentions for' : 'yourself up for'} tomorrow.`
                }
              </p>

              {moodAfter > moodBefore && (
                <Badge className="mb-6 bg-primary/20 text-primary border-0">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Mood improved by {moodAfter - moodBefore} points
                </Badge>
              )}

              <Button onClick={onClose} variant="outline" className="gap-2">
                Close
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
